import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { Section } from './ui'
import type { AdminNote, AttachedToType, NoteCategory, Priority } from '../lib/types'

const CATEGORIES: NoteCategory[] = ['general', 'operations', 'billing', 'credentialing', 'support']
const PRIORITIES: Priority[] = ['critical', 'high', 'medium', 'low']

const PRIORITY_COLORS: Record<Priority, string> = {
  critical: 'badge-red',
  high: 'badge-orange',
  medium: 'badge-amber',
  low: 'badge-grey',
}

/** Unified Notes/Tasks panel — one fetch, one shared data model, rendered as
 * two sections (Tasks / Notes) to match the entity-page template. A task's
 * `priority` badge is deliberately separate from and never derived from the
 * entity's own Health/Risk badge shown elsewhere on the page. `assigned_team`
 * and `watchers` are accepted by the API but not exposed here yet. */
export function NotesPanel({
  attachedToType, attachedToId, organizationId,
}: {
  attachedToType: AttachedToType
  attachedToId: string
  organizationId?: string | null
}) {
  const { me } = useAuth()
  const [notes, setNotes] = useState<AdminNote[]>([])
  const [loading, setLoading] = useState(true)
  const [addingTask, setAddingTask] = useState(false)
  const [addingNote, setAddingNote] = useState(false)

  async function load() {
    setLoading(true)
    const rows = await api(`/admin/notes?attached_to_type=${attachedToType}&attached_to_id=${attachedToId}`)
    setNotes(rows)
    setLoading(false)
  }

  useEffect(() => { load() }, [attachedToType, attachedToId])

  async function toggleTaskStatus(n: AdminNote) {
    await api(`/admin/notes/${n.id}`, { method: 'PATCH', body: JSON.stringify({ status: n.status === 'done' ? 'open' : 'done' }) })
    load()
  }

  async function remove(id: string) {
    await api(`/admin/notes/${id}`, { method: 'DELETE' })
    load()
  }

  if (loading) return <div className="muted" style={{ fontSize: 13 }}>Loading notes &amp; tasks…</div>

  const tasks = notes.filter((n) => n.is_task)
  const plainNotes = notes.filter((n) => !n.is_task)

  return (
    <>
      <Section title="Tasks" note={`${tasks.length}`}>
        <div className="row-list">
          {tasks.map((t) => (
            <div key={t.id} className="row" style={{ cursor: 'default' }}>
              <span>
                <button
                  className={`btn small outline`}
                  style={{ marginRight: 8, padding: '0 8px' }}
                  onClick={() => toggleTaskStatus(t)}
                  title={t.status === 'done' ? 'Reopen' : 'Mark done'}
                >
                  {t.status === 'done' ? '↺' : '✓'}
                </button>
                <span className={`badge ${PRIORITY_COLORS[t.priority]}`} style={{ marginRight: 6 }}>{t.priority}</span>
                <span className="badge badge-grey" style={{ marginRight: 6 }}>{t.category}</span>
                <span style={{ textDecoration: t.status === 'done' ? 'line-through' : 'none' }}>{t.content}</span>
              </span>
              <span className="muted" style={{ fontSize: 12 }}>
                {t.due_at ? `Due ${new Date(t.due_at).toLocaleDateString()}` : ''}
                <button className="btn small outline" style={{ marginLeft: 8 }} onClick={() => remove(t.id)}>Delete</button>
              </span>
            </div>
          ))}
          {tasks.length === 0 && <div className="empty">No tasks yet.</div>}
        </div>
        {addingTask ? (
          <NoteForm
            isTask
            organizationId={organizationId}
            selfId={me?.id}
            onCancel={() => setAddingTask(false)}
            onSubmit={async (payload) => {
              await api('/admin/notes', {
                method: 'POST',
                body: JSON.stringify({ attached_to_type: attachedToType, attached_to_id: attachedToId, organization_id: organizationId, ...payload }),
              })
              setAddingTask(false)
              load()
            }}
          />
        ) : (
          <button className="btn small outline" style={{ marginTop: 8 }} onClick={() => setAddingTask(true)}>+ Add task</button>
        )}
      </Section>

      <Section title="Notes" note={`${plainNotes.length}`}>
        <div className="row-list">
          {plainNotes.map((n) => (
            <div key={n.id} className="row" style={{ cursor: 'default' }}>
              <span>
                <span className="badge badge-grey" style={{ marginRight: 6 }}>{n.category}</span>
                {n.content}
              </span>
              <span className="muted" style={{ fontSize: 12 }}>
                {new Date(n.created_at).toLocaleDateString()}
                <button className="btn small outline" style={{ marginLeft: 8 }} onClick={() => remove(n.id)}>Delete</button>
              </span>
            </div>
          ))}
          {plainNotes.length === 0 && <div className="empty">No notes yet.</div>}
        </div>
        {addingNote ? (
          <NoteForm
            isTask={false}
            organizationId={organizationId}
            selfId={me?.id}
            onCancel={() => setAddingNote(false)}
            onSubmit={async (payload) => {
              await api('/admin/notes', {
                method: 'POST',
                body: JSON.stringify({ attached_to_type: attachedToType, attached_to_id: attachedToId, organization_id: organizationId, ...payload }),
              })
              setAddingNote(false)
              load()
            }}
          />
        ) : (
          <button className="btn small outline" style={{ marginTop: 8 }} onClick={() => setAddingNote(true)}>+ Add note</button>
        )}
      </Section>
    </>
  )
}

function NoteForm({
  isTask, selfId, onCancel, onSubmit,
}: {
  isTask: boolean
  organizationId?: string | null
  selfId?: string
  onCancel: () => void
  onSubmit: (payload: { content: string; category: NoteCategory; is_task: boolean; priority: Priority; assigned_to?: string | null; due_at?: string | null }) => Promise<void>
}) {
  const [content, setContent] = useState('')
  const [category, setCategory] = useState<NoteCategory>('general')
  const [priority, setPriority] = useState<Priority>('medium')
  const [assignToMe, setAssignToMe] = useState(isTask)
  const [dueAt, setDueAt] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!content.trim()) return
    setBusy(true)
    try {
      await onSubmit({
        content: content.trim(),
        category,
        is_task: isTask,
        priority,
        assigned_to: isTask && assignToMe ? selfId : null,
        due_at: isTask && dueAt ? new Date(dueAt).toISOString() : null,
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="card" style={{ marginTop: 8, padding: 12 }}>
      <div className="field">
        <label>{isTask ? 'Task' : 'Note'}</label>
        <textarea className="inp" rows={2} value={content} onChange={(e) => setContent(e.target.value)} required autoFocus />
      </div>
      <div className="form-grid" style={{ marginTop: 8 }}>
        <div className="field">
          <label>Category</label>
          <select className="inp" value={category} onChange={(e) => setCategory(e.target.value as NoteCategory)}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {isTask && (
          <>
            <div className="field">
              <label>Priority</label>
              <select className="inp" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Due date</label>
              <input className="inp" type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
            </div>
            <div className="field">
              <label>
                <input type="checkbox" checked={assignToMe} onChange={(e) => setAssignToMe(e.target.checked)} style={{ marginRight: 6 }} />
                Assign to me
              </label>
            </div>
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="btn small" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        <button className="btn small outline" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}
